import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, X, Clock, Plane, UserMinus, Coffee, Play, Square,
  Loader2, AlertTriangle, Inbox, FileText, FileImage, Download, Search,
  Wrench, ArrowRight, Gift, Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  searchWorkers, getCompanyWorkers, searchCompanies, getEventsByRange, updateEvent, deleteEvent,
  listCompanyDocuments, downloadDocument, getWorkplaces,
  listCorrections, reviewCorrection, listAgreements,
  getWorkHours, getScheduleVersionHistory, jsDowToBackend, isDeferred, deferredLabel,
  buildGeoInfoWithApprover, parseMotivo,
  buildGeoInfoWithRecoverable, parseRecoverableFlag,
  addHourBalance,
  EVENT_TYPES, EVENT_TYPE_LABELS, APPROVAL_STATUS,
} from '../services/api';
import AbsenceApprovalModal, { computeAbsenceMinutes } from '../components/AbsenceApprovalModal';
import { useToast } from '../components/Toast';
import { FEEDBACK, fmtDayRange, requestSubject, eventNoun } from '../services/feedbackMessages';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, dayKeyInZone, dowInZone, formatHmInZone,
  dayKeysBetween,
} from '../lib/workerTime';
import LocationCell from '../components/LocationCell';
import { splitReasonGeo } from '../lib/geoInfo';
import { USER_TYPE_LABELS, filterWorkersForUser, canDeleteEvent, workerInSite } from '../lib/permissions';
import {
  isDeleteRequestEvent, isClockDeleteRequest, isAnyDeleteRequest,
  stripDeleteRequestFlag, findPeriodPartner,
} from '../lib/dayStatus';
import { loadVacationCapCheck } from '../services/vacationCap';
import { yearCountWindow } from '../lib/vacationCounter';

const EVENT_META = {
  [EVENT_TYPES.CLOCK_IN]:         { label: 'Entrada',            icon: Play,        color: 'text-success' },
  [EVENT_TYPES.CLOCK_OUT]:        { label: 'Salida',             icon: Square,      color: 'text-primary-darker' },
  [EVENT_TYPES.VACATION_START]:   { label: 'Inicio vacaciones',  icon: Plane,       color: 'text-accent-dark' },
  [EVENT_TYPES.VACATION_END]:     { label: 'Fin vacaciones',     icon: Plane,       color: 'text-accent-dark' },
  [EVENT_TYPES.SICK_LEAVE_START]: { label: 'Inicio día completo',   icon: UserMinus, color: 'text-danger' },
  [EVENT_TYPES.SICK_LEAVE_END]:   { label: 'Fin día completo',      icon: UserMinus, color: 'text-danger' },
  [EVENT_TYPES.PAUSE_START]:      { label: 'Inicio pausa',       icon: Coffee,      color: 'text-warning' },
  [EVENT_TYPES.PAUSE_END]:        { label: 'Fin pausa',          icon: Coffee,      color: 'text-warning' },
  [EVENT_TYPES.COMPENSATION_START]: { label: 'Inicio compensación', icon: Gift, color: 'text-violet-600' },
  [EVENT_TYPES.COMPENSATION_END]:   { label: 'Fin compensación',    icon: Gift, color: 'text-violet-600' },
  [EVENT_TYPES.ABSENCE_START]:    { label: 'Inicio por horas',    icon: Clock, color: 'text-warning' },
  [EVENT_TYPES.ABSENCE_END]:      { label: 'Fin por horas',       icon: Clock, color: 'text-warning' },
};

// Ausencia por horas: se recupera siempre (la bolsa la cuenta como deuda).
const HOURLY_ABSENCE_APPROVE_TITLE = 'Aprobar la ausencia por horas. Se recupera: la jornada del día no se reduce y las horas ausentes salen como deuda en la bolsa.';

// Día y hora de reloj del instante en la zona del TRABAJADOR (`tz`), no la
// del navegador del admin. El día se pinta en UTC para no desplazarlo.
const fmtDayKey = (key, opts) => (key
  ? new Date(`${key}T00:00:00Z`).toLocaleDateString('es-ES', { ...opts, timeZone: 'UTC' })
  : '');
const fmtFull = (iso, tz = DEFAULT_TIME_ZONE) => {
  const key = dayKeyInZone(iso, tz);
  if (!key) return '';
  return `${fmtDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' })}, ${formatHmInZone(iso, tz)}`;
};

// Las 4 pestañas: cada una decide qué tipos de evento mostrar (o si la fuente
// son las correcciones, que viven en otra colección).
const TABS = [
  { key: 'correcciones',  label: 'Correcciones',           icon: Wrench,      source: 'correction' },
  { key: 'vacaciones',    label: 'Vacaciones',             icon: Plane,       source: 'event', match: (t) => [2, 3].includes(t) },
  { key: 'bajas',         label: 'Días completos',         icon: UserMinus, source: 'event', match: (t) => [4, 5].includes(t) },
  { key: 'ausencias',     label: 'Ausencias por horas',    icon: Clock,       source: 'event', match: (t) => [10, 11].includes(t) },
  { key: 'manuales',      label: 'Fichajes manuales',      icon: Play,        source: 'event', match: (t) => [0, 1].includes(t) },
];

const fileIcon = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext)) return FileImage;
  return FileText;
};

// `onError(err)`: quién avisa del fallo (el toast de la página).
const triggerDownload = async (doc, onError) => {
  try {
    const blob = await downloadDocument(doc.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.filename || `documento-${doc.id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (err) {
    onError?.(err);
  }
};

const AprobacionesPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const onDownloadError = (err) => toast.error(err, FEEDBACK.downloadFailed);
  const [workers, setWorkers] = useState([]);
  const [events, setEvents] = useState([]);
  const [corrections, setCorrections] = useState([]);
  // Convenios del sistema: necesarios para conocer el cupo anual de
  // vacaciones (`vacationDays`) y el `holidayType` de cada trabajador al
  // aprobar un evento VAC_START/VAC_END (ver confirmVacationCapOk).
  const [agreements, setAgreements] = useState([]);
  const [docsByUploader, setDocsByUploader] = useState(new Map());
  const [workplacesById, setWorkplacesById] = useState(new Map());
  // Mapa id→nombre de empresa para pintar el selector de filtro. Solo
  // tiene sentido cuando el usuario ve varias empresas (super admin).
  const [companiesById, setCompaniesById] = useState(new Map());
  // workhours indexado por workerId → Map<dayOfWeek, slot>. Lo usamos para
  // detectar eventos que caen en un día de descanso del trabajador y
  // ofrecer aprobarlos como horas extras o como compensación.
  const [workhoursByWorker, setWorkhoursByWorker] = useState(new Map());
  // Histórico de versiones de jornada por trabajador (railway only). Si el
  // backend tiene los seeds lanzados, cada array trae todas las
  // ScheduleVersion con sus assignSlots. El modal de aprobación lo usa para
  // calcular las horas de la ausencia con la jornada vigente EN LA FECHA
  // del periodo, no con la jornada actual. Si está vacío, el fallback es
  // el workhours actual.
  const [scheduleHistoryByWorker, setScheduleHistoryByWorker] = useState(new Map());
  // Estado de la carga del horario: el histórico llega en segundo plano y
  // la petición de workhours puede fallar. Antes un fallo se convertía en
  // "horario vacío" en silencio y el modal de ausencias decía "sin jornada
  // configurada". Ahora se distingue cargando / fallo / listo.
  const [scheduleHistoryLoaded, setScheduleHistoryLoaded] = useState(false);
  const [workhoursFailed, setWorkhoursFailed] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('correcciones');
  const [actioningId, setActioningId] = useState(null);

  // Modal de confirmación de ausencias justificadas. Cuando se abre, contiene
  // { event (START), endEvent (END o null), workhours } para que el modal
  // calcule duración y muestre preview de la bolsa. El admin decide ahí si
  // descuenta o no, y al confirmar dispara handleAbsenceApproval con el flag.
  const [absenceModal, setAbsenceModal] = useState(null);

  // Filtros adicionales (compartidos por todas las pestañas).
  // `filterCompanyId` solo tiene sentido cuando el usuario ve varias empresas
  // (super admin sin companyId). Para el resto queda invisible.
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterWorkerId, setFilterWorkerId] = useState('');
  const [filterWorkplaceId, setFilterWorkplaceId] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [filterQuery, setFilterQuery] = useState('');

  const isMaster = user?.type === 5;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const workersData = (isMaster || !user?.companyId)
        ? await searchWorkers('')
        : await getCompanyWorkers(user.companyId);
      const list = filterWorkersForUser(Array.isArray(workersData) ? workersData : [], user);
      setWorkers(list);

      // Convenios del sistema (en paralelo, no bloquea si falla). Lo
      // necesitamos para detectar excesos de cupo al aprobar vacaciones.
      listAgreements()
        .then((ags) => setAgreements(Array.isArray(ags) ? ags : []))
        .catch(() => setAgreements([]));

      // Ventana de carga: todo el año natural en curso (1 ene → 31 dic).
      // Las solicitudes de vacaciones suelen pedirse con meses de antelación,
      // así que si solo cargáramos hasta hoy no aparecerían las vacaciones
      // futuras del año (ej. agosto pedida en mayo).
      // Con MARGEN (yearCountWindow, ±60 días): un periodo que cruza el
      // cambio de año (28-dic → 3-ene) tiene un extremo en cada año; sin
      // margen salía partido (dos "sueltos") y su solicitud de eliminación
      // no encontraba la pareja. Cubre también el ±1 día de zona horaria.
      const yearWin = yearCountWindow(new Date().getFullYear());
      const rangeStart = yearWin.fetchFrom;
      const rangeEnd = yearWin.fetchTo;

      const companyIds = [...new Set(list.map((w) => w.companyId).filter(Boolean))];

      // Nombres de empresas para el selector del filtro. Solo cargamos si
      // hay más de una en juego — para admins de una sola empresa no aporta.
      if (companyIds.length > 1) {
        searchCompanies('')
          .then((arr) => {
            const m = new Map();
            for (const c of (Array.isArray(arr) ? arr : [])) {
              if (c?.id != null) m.set(Number(c.id), c.name || `Empresa #${c.id}`);
            }
            setCompaniesById(m);
          })
          .catch(() => setCompaniesById(new Map()));
      } else {
        setCompaniesById(new Map());
      }

      const [allEvents, docsLists, wplaceLists, allCorrections, allWorkhours] = await Promise.all([
        Promise.all(list.map(async (w) => {
          try {
            const evs = await getEventsByRange(w.id, rangeStart, rangeEnd);
            return (Array.isArray(evs) ? evs : []).map((e) => ({ ...e, _worker: w }));
          } catch { return []; }
        })),
        Promise.all(companyIds.map((cid) => listCompanyDocuments(cid).catch(() => []))),
        Promise.all(companyIds.map((cid) => getWorkplaces(cid).catch(() => []))),
        // Correcciones: una llamada por trabajador. Aceptamos array directo o
        // wrappers comunes ({ corrections: [...] } / { data: [...] }). Si el
        // endpoint falla, lo logueamos para no romper la página entera pero
        // tampoco silenciar el problema.
        Promise.all(list.map(async (w) => {
          try {
            const cs = await listCorrections(w.id);
            const arr = Array.isArray(cs) ? cs
              : Array.isArray(cs?.corrections) ? cs.corrections
              : Array.isArray(cs?.data) ? cs.data
              : [];
            if (!Array.isArray(cs) && cs != null) {
              // eslint-disable-next-line no-console
              console.warn('[corrections] respuesta inesperada para worker', w.id, cs);
            }
            return arr.map((c) => ({ ...c, _worker: w }));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[corrections] error para worker', w.id, err);
            return [];
          }
        })),
        // Workhours por trabajador: una llamada por id. Lo usamos para saber
        // si un evento pendiente cae en su día de descanso.
        Promise.all(list.map(async (w) => {
          try {
            const slots = await getWorkHours(w.id);
            return { workerId: w.id, slots: Array.isArray(slots) ? slots : [] };
          } catch {
            // NO se cae a "sin horario": se marca como fallo.
            return { workerId: w.id, slots: null, failed: true };
          }
        })),
      ]);
      setEvents(allEvents.flat());
      setCorrections(allCorrections.flat());

      const whMap = new Map();
      const whFailed = new Set();
      for (const { workerId, slots, failed } of allWorkhours) {
        if (failed) { whFailed.add(Number(workerId)); continue; }
        const byDow = new Map();
        for (const s of slots) byDow.set(Number(s.dayOfWeek), s);
        whMap.set(Number(workerId), byDow);
      }
      setWorkhoursByWorker(whMap);
      setWorkhoursFailed(whFailed);
      setScheduleHistoryLoaded(false);

      // Histórico de jornadas por trabajador. Lo cargamos en SEGUNDO PLANO
      // (no bloquea la pantalla) y tolerante a fallos: si el endpoint no
      // existe o devuelve 404 — caso típico cuando el seed multi-schedule
      // todavía no se ha lanzado — quedamos con map vacío y el modal cae
      // al workhours actual. Esto es lo único específico de railway
      // respecto a main; el resto del flujo es idéntico.
      Promise.all(list.map(async (w) => {
        try {
          const hist = await getScheduleVersionHistory(w.id);
          return { workerId: w.id, history: Array.isArray(hist) ? hist : [] };
        } catch {
          return { workerId: w.id, history: [] };
        }
      })).then((entries) => {
        const m = new Map();
        for (const { workerId, history } of entries) {
          if (history.length > 0) m.set(Number(workerId), history);
        }
        setScheduleHistoryByWorker(m);
        setScheduleHistoryLoaded(true);
      }).catch(() => { setScheduleHistoryByWorker(new Map()); setScheduleHistoryLoaded(true); });

      const map = new Map();
      for (const docs of docsLists) {
        for (const d of (Array.isArray(docs) ? docs : [])) {
          const key = Number(d.uploaderId);
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(d);
        }
      }
      for (const arr of map.values()) {
        arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      }
      setDocsByUploader(map);

      const wpMap = new Map();
      for (const arr of wplaceLists) {
        for (const wp of (Array.isArray(arr) ? arr : [])) wpMap.set(Number(wp.id), wp);
      }
      setWorkplacesById(wpMap);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user, isMaster]);

  useEffect(() => { load(); }, [load]);

  // Filtra cualquier item (event o correction) por los filtros compartidos.
  const passesSharedFilters = useCallback((item) => {
    const q = filterQuery.trim().toLowerCase();
    const targetCompanyId = filterCompanyId ? Number(filterCompanyId) : null;
    const targetWorkerId = filterWorkerId ? Number(filterWorkerId) : null;
    const targetWorkplaceId = filterWorkplaceId ? String(filterWorkplaceId) : null;

    if (targetCompanyId && Number(item._worker?.companyId) !== targetCompanyId) return false;
    if (targetWorkerId && item._worker?.id !== targetWorkerId) return false;
    if (targetWorkplaceId && !workerInSite(item._worker, targetWorkplaceId)) return false;
    // Filtro de fechas por DÍA del trabajador (su zona), no por la hora del
    // navegador del admin: así un evento de las 00:30 de Madrid cae en su día.
    if (filterDateStart || filterDateEnd) {
      const key = dayKeyInZone(item.timestamp || item.createdAt || 0, timeZoneForWorker(item._worker));
      if (!key) return false;
      if (filterDateStart && key < filterDateStart) return false;
      if (filterDateEnd && key > filterDateEnd) return false;
    }
    if (q) {
      const name = (item._worker?.name || '').toLowerCase();
      const email = (item._worker?.email || '').toLowerCase();
      const nif = (item._worker?.nif || '').toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !nif.includes(q)) return false;
    }
    return true;
  }, [filterCompanyId, filterWorkerId, filterWorkplaceId, filterDateStart, filterDateEnd, filterQuery]);

  const pendingEvents = useMemo(() => {
    return events
      .filter((e) => (e.approvalStatus ?? 1) === APPROVAL_STATUS.PENDING)
      .filter(passesSharedFilters);
  }, [events, passesSharedFilters]);

  // Solicitudes de eliminación de FICHAJES sueltos (entrada/salida) que el
  // trabajador pide desde su Informe horario. A diferencia de los periodos,
  // estos fichajes siguen APROBADOS (y contando en la jornada y en la bolsa)
  // mientras no se resuelvan: por eso NO aparecen en `pendingEvents` y hay
  // que recogerlos aparte. Los que ya estén PENDING los trae pendingEvents.
  const clockDeleteRequests = useMemo(() => {
    return events
      .filter((e) => isClockDeleteRequest(e) && (e.approvalStatus ?? 1) !== APPROVAL_STATUS.PENDING)
      .filter(passesSharedFilters);
  }, [events, passesSharedFilters]);

  // Lee approvalStatus tolerando variantes (algunos backends usan snake_case
  // o lo llaman "status"). Si NADA está presente, asumimos pendiente.
  const readApprovalStatus = (c) => {
    const candidates = [c?.approvalStatus, c?.approval_status, c?.ApprovalStatus, c?.status];
    for (const v of candidates) {
      if (v !== undefined && v !== null && v !== '') return Number(v);
    }
    return APPROVAL_STATUS.PENDING;
  };

  const pendingCorrections = useMemo(() => {
    return corrections
      .filter((c) => {
        const s = readApprovalStatus(c);
        return s !== APPROVAL_STATUS.APPROVED && s !== APPROVAL_STATUS.REJECTED;
      })
      .filter(passesSharedFilters)
      .sort((a, b) => new Date(b.createdAt || b.timestamp || 0) - new Date(a.createdAt || a.timestamp || 0));
  }, [corrections, passesSharedFilters]);

  // Empareja eventos START + END por orden cronológico dentro de cada
  // trabajador. Devuelve una lista mixta de:
  //   · { kind: 'period', start, end, _worker } cuando ambos extremos
  //     están entre los eventos (lo normal: el trabajador pide un periodo
  //     y los dos extremos quedan en PENDING).
  //   · { kind: 'single', event, _worker } cuando solo aparece UNO de los
  //     dos (típicamente START sin END todavía, o END huérfano por borrado
  //     parcial). El admin puede aprobar el individual sin perder el caso.
  // Esto permite que la pestaña muestre UN solo ítem por periodo y que
  // "Aprobar" cierre los dos extremos a la vez.
  const buildPeriodItems = (events, startType, endType) => {
    const byWorker = new Map();
    for (const e of events) {
      if (e.eventType !== startType && e.eventType !== endType) continue;
      const k = Number(e.workerId);
      if (!byWorker.has(k)) byWorker.set(k, []);
      byWorker.get(k).push(e);
    }
    const out = [];
    for (const [, list] of byWorker) {
      list.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      let pendingStart = null;
      for (const e of list) {
        if (e.eventType === startType) {
          if (pendingStart) {
            // START anterior sin END → sale solo.
            out.push({ kind: 'single', event: pendingStart, _worker: pendingStart._worker });
          }
          pendingStart = e;
        } else {
          if (pendingStart) {
            out.push({ kind: 'period', start: pendingStart, end: e, _worker: pendingStart._worker });
            pendingStart = null;
          } else {
            // END huérfano sin START previo.
            out.push({ kind: 'single', event: e, _worker: e._worker });
          }
        }
      }
      if (pendingStart) {
        out.push({ kind: 'single', event: pendingStart, _worker: pendingStart._worker });
      }
    }
    return out;
  };

  // Tabs que tienen pares START/END. Para fichajes manuales (CLOCK_IN/OUT)
  // NO emparejamos: son eventos sueltos del día, no un periodo lógico.
  const PERIOD_TAB_TYPES = {
    vacaciones: [EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END],
    bajas:      [EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END],
    ausencias:  [EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END],
  };

  // Lista que se renderiza según la pestaña activa.
  const visibleItems = useMemo(() => {
    const def = TABS.find((t) => t.key === tab);
    if (!def) return [];
    if (def.source === 'correction') return pendingCorrections;
    const filtered = pendingEvents
      .filter((e) => def.match(e.eventType));
    const periodTypes = PERIOD_TAB_TYPES[tab];
    if (periodTypes) {
      // Agrupamos start+end del trabajador como un único "periodo" para
      // que aprobar/rechazar afecte a ambos extremos a la vez.
      const items = buildPeriodItems(filtered, periodTypes[0], periodTypes[1]);
      // Ordenar más reciente primero (por el START si es periodo).
      items.sort((a, b) => {
        const ta = a.kind === 'period' ? a.start.timestamp : a.event.timestamp;
        const tb = b.kind === 'period' ? b.start.timestamp : b.event.timestamp;
        return new Date(tb) - new Date(ta);
      });
      return items;
    }
    // Fichajes manuales: además de los pendientes, las solicitudes de
    // eliminación de fichajes ya aprobados (que no son "pendientes").
    const extra = tab === 'manuales' ? clockDeleteRequests : [];
    return [...filtered, ...extra]
      .map((e) => ({ kind: 'single', event: e, _worker: e._worker }))
      .sort((a, b) => new Date(b.event.timestamp) - new Date(a.event.timestamp));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, pendingEvents, pendingCorrections, clockDeleteRequests]);

  // Contadores por pestaña (sin aplicar filtros, para no esconder badges al
  // filtrar por trabajador y dejar al admin ciego).
  const counts = useMemo(() => {
    const isEventPending = (s) => (s ?? 1) === APPROVAL_STATUS.PENDING;
    const pendingEvs = events.filter((e) => isEventPending(e.approvalStatus));
    // Para las tres pestañas de periodo, contamos PERIODOS (no eventos
    // individuales): un periodo START+END suma 1, un huérfano también 1.
    // Así el badge coincide con lo que el admin verá en la lista.
    const countPeriodItems = (filtered, startType, endType) =>
      buildPeriodItems(filtered, startType, endType).length;
    return {
      correcciones: corrections.filter((c) => {
        const s = readApprovalStatus(c);
        return s !== APPROVAL_STATUS.APPROVED && s !== APPROVAL_STATUS.REJECTED;
      }).length,
      vacaciones: countPeriodItems(pendingEvs, EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END),
      bajas: countPeriodItems(pendingEvs, EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END),
      ausencias: countPeriodItems(pendingEvs, EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END),
      // + las solicitudes de eliminación de fichajes aprobados: no están
      // PENDING, pero el admin tiene que resolverlas igual.
      manuales: pendingEvs.filter((e) => [0, 1].includes(e.eventType)).length
        + events.filter((e) => isClockDeleteRequest(e) && (e.approvalStatus ?? 1) !== APPROVAL_STATUS.PENDING).length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, corrections]);

  const totalPending = counts.correcciones + counts.vacaciones + counts.bajas + counts.ausencias + counts.manuales;

  // Detecta si el evento cae en un día que el trabajador tiene como descanso
  // (sin slot en /api/workhours para ese dayOfWeek). Si no tenemos workhours
  // cargados aún, devolvemos false para no marcar todo como descanso.
  // Zona del trabajador dueño de un evento/corrección (fila `_worker` o, si
  // no viene, la lista de trabajadores cargada). Sin datos → Madrid.
  const tzOf = (item) => timeZoneForWorker(
    item?._worker || workers.find((w) => Number(w.id) === Number(item?.workerId)) || null
  );

  // Para los toasts de confirmación: nombre del trabajador y fechas (en su zona).
  const workerNameOf = (item) =>
    (item?._worker || workers.find((w) => Number(w.id) === Number(item?.workerId)) || null)?.name || null;
  const whenOf = (startEv, endEv = null) => {
    const tz = tzOf(startEv);
    const isRange = endEv && ![EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END].includes(startEv.eventType);
    if (isRange) return fmtDayRange(dayKeyInZone(startEv.timestamp, tz), dayKeyInZone(endEv.timestamp, tz));
    const day = fmtDayRange(dayKeyInZone(startEv.timestamp, tz));
    if (endEv) return `${day}, ${formatHmInZone(startEv.timestamp, tz)}–${formatHmInZone(endEv.timestamp, tz)}`;
    // Solo los fichajes y las ausencias por horas tienen una hora con sentido.
    const timed = [
      EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT, EVENT_TYPES.PAUSE_START, EVENT_TYPES.PAUSE_END,
      EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END,
    ].includes(startEv.eventType);
    return timed ? `${day}, ${formatHmInZone(startEv.timestamp, tz)}` : day;
  };

  const eventOnRestDay = (e) => {
    const wh = workhoursByWorker.get(Number(e._worker?.id || e.workerId));
    if (!wh) return false;
    // Día de la semana EN LA ZONA DEL TRABAJADOR.
    const dow = jsDowToBackend(dowInZone(e.timestamp, tzOf(e)));
    return !wh.has(dow);
  };

  // Antes de aprobar vacaciones, comprueba cómo quedaría el cupo anual del
  // trabajador DESPUÉS de aprobar ESTA solicitud (src/services/vacationCap.js
  // → checkVacationCap, el mismo cómputo que los contadores: emparejado
  // compartido, festivos, histórico de horarios y reparto por año). Si se
  // pasa, pide confirmación explícita (no bloquea). Si no se puede comprobar
  // (fallo al cargar eventos u horario, sin horario) avisa y NO calcula nada:
  // nunca se inventa un horario vacío.
  // `startEv` / `endEv`: extremos del periodo (endEv null si se aprueba un
  // extremo suelto; se busca su pareja entre los eventos cargados).
  // Devuelve true si se puede seguir con la aprobación.
  const confirmVacationCapOk = async (startEv, endEv = null) => {
    const isVac = (e) => e && (e.eventType === EVENT_TYPES.VACATION_START || e.eventType === EVENT_TYPES.VACATION_END);
    if (!isVac(startEv)) return true;
    const partner = endEv || findPeriodPartner(events, startEv);
    const [first, last] = partner && new Date(partner.timestamp) < new Date(startEv.timestamp)
      ? [partner, startEv] : [startEv, partner];
    const worker = workers.find((w) => Number(w.id) === Number(startEv.workerId)) || startEv._worker || null;
    const request = {
      start: first.timestamp,
      end: (last || first).timestamp,
      startId: first.eventType === EVENT_TYPES.VACATION_START ? first.id : undefined,
      endId: last && last.eventType === EVENT_TYPES.VACATION_END ? last.id : undefined,
    };
    let result;
    try {
      result = await loadVacationCapCheck({ worker, request, agreements, verb: 'aprobar' });
    } catch {
      result = { status: 'error', message: 'No se pudo comprobar el cupo anual de vacaciones.' };
    }
    if (result.status === 'error') {
      toast.warning(`${result.message} Revisa el cupo en la ficha del trabajador.`);
      return true;
    }
    if (result.status === 'ok' && result.message) {
      return window.confirm(`${result.message}\n\n¿Aprobar igualmente?`);
    }
    return true;
  };

  // Aprobación / rechazo. Si `opts.countAsExtra` viene definido (true/false),
  // marca el evento con un sufijo en geoInfo (`;extra=1` ó `;extra=0`). El
  // cómputo de extras en informes lee ese flag para distinguir un fichaje
  // en día de descanso aprobado como extras de uno aprobado como jornada
  // normal. Workaround hasta que el backend tenga un campo dedicado.
  // Detecta si el evento es una "solicitud de eliminación" de un periodo
  // (workaround con sufijo `;reqdelete=1` en geoInfo). Vale para TODOS los
  // tipos de periodo: vacaciones, día completo, por horas y compensación.
  // Antes solo se miraban las vacaciones y "aprobar" la eliminación de una
  // ausencia la APROBABA (el periodo seguía vivo con el flag).
  const isPeriodDeleteRequest = (e) => isDeleteRequestEvent(e);

  // Cualquier solicitud de eliminación: la de un PERIODO (dos extremos) o la
  // de un FICHAJE suelto (entrada/salida) que el trabajador pide desde su
  // Informe horario de la app. La del fichaje llega SIN tocar approvalStatus:
  // sigue APROBADO — y contando en su jornada y en su bolsa — hasta que se
  // resuelva aquí. Eso es intencionado: el registro de jornada es un
  // documento legal y no deja de valer porque alguien pida borrarlo.
  const isEventDeleteRequest = (e) => isAnyDeleteRequest(e);

  // Extremo opuesto del mismo periodo (mismo emparejado que los contadores:
  // src/lib/dayStatus.js → findPeriodPartner).
  const findPartner = (event) => findPeriodPartner(events, event);

  // Aprobar o rechazar una solicitud de eliminación de un periodo.
  //   - Aprobar  → DELETE de los DOS eventos (START + END). El periodo
  //     desaparece del calendario del trabajador.
  //   - Rechazar → quita el flag `;reqdelete=1` de ambos extremos y los
  //     deja como APROBADOS (el periodo sigue como estaba). El geoInfo se
  //     manda con stripDeleteRequestFlag: nunca null / '' (el backend hace
  //     `geoInfo || actual` y conservaría el flag).
  const handleDeleteRequest = async (event, approved) => {
    setActioningId(`event-${event.id}`);
    try {
      const partner = findPartner(event);
      if (approved) {
        await deleteEvent(event.id);
        if (partner) await deleteEvent(partner.id);
        setEvents((prev) => prev.filter((e) => e.id !== event.id && e.id !== partner?.id));
      } else {
        const stripFlag = stripDeleteRequestFlag;
        await updateEvent(event.id, {
          workerId: event.workerId,
          eventType: event.eventType,
          timestamp: event.timestamp,
          geoInfo: stripFlag(event.geoInfo),
          approvalStatus: APPROVAL_STATUS.APPROVED,
        });
        if (partner) {
          await updateEvent(partner.id, {
            workerId: partner.workerId,
            eventType: partner.eventType,
            timestamp: partner.timestamp,
            geoInfo: stripFlag(partner.geoInfo),
            approvalStatus: APPROVAL_STATUS.APPROVED,
          });
        }
        setEvents((prev) => prev.map((e) => {
          if (e.id === event.id) return { ...e, geoInfo: stripFlag(e.geoInfo), approvalStatus: APPROVAL_STATUS.APPROVED };
          if (partner && e.id === partner.id) return { ...e, geoInfo: stripFlag(e.geoInfo), approvalStatus: APPROVAL_STATUS.APPROVED };
          return e;
        }));
      }
      const [first, last] = partner && new Date(partner.timestamp) < new Date(event.timestamp)
        ? [partner, event] : [event, partner];
      // Un fichaje suelto no tiene pareja ni es un "periodo": su aviso se
      // redacta aparte (la entrada / la salida, no "el periodo de …").
      toast.success(isClockDeleteRequest(event)
        ? FEEDBACK.clockDeleteReviewed({
            approved, workerName: workerNameOf(event), when: whenOf(event),
            noun: eventNoun(event.eventType),
          })
        : FEEDBACK.vacationDeleteReviewed({
            approved, workerName: workerNameOf(event), when: whenOf(first, last),
            subject: requestSubject(event.eventType),
          }));
    } catch (err) {
      toast.error(err, 'No se pudo resolver la solicitud de eliminación.');
    } finally {
      setActioningId(null);
    }
  };

  // ── Ausencias justificadas recuperables ─────────────────────────────────
  // AUSENCIAS POR HORAS (ABSENCE): se recuperan SIEMPRE (decisión de
  // producto 2026-09-22). La bolsa ya las cuenta como deuda (no reducen la
  // jornada esperada, ver makeBalanceDayResolver), así que NO se pregunta
  // recuperable/no recuperable ni se crea offset (lo duplicaría): se aprueba
  // START + END y se marcan `;recoverable=1` para que las vistas lo muestren
  // (handleHourlyAbsenceApproval).
  //
  // DÍA COMPLETO (SICK_LEAVE): cuando el admin aprueba el START, puede marcar
  // las horas como "recuperables": el trabajador debe esas horas a la
  // empresa. Mecánica:
  //   1) Buscar el END del par (mismo workerId, mismo tipo, después de START).
  //      Si no se encuentra (END todavía no creado), no podemos calcular
  //      duración → caemos al flujo normal de aprobación.
  //   2) Calcular minutos de la ausencia: duración de la jornada del
  //      trabajador cada día (in1→out1 + in2→out2). No 24h.
  //   3) Crear un offset manual en hourBalance con `offset` negativo (deuda).
  //   4) Marcar START y END con geoInfo `;recoverable=1` para que el resto
  //      de vistas (Informe horario, ficha del trabajador) lo muestren.
  //
  // Si recoverable=false, solo se marca con `;recoverable=0` y se aprueba.
  // El comportamiento contra la bolsa es exactamente el de siempre (no
  // afecta porque las ausencias justificadas nunca cuentan como trabajo).
  const findAbsenceEnd = (startEvent, endType) => {
    const sameWorker = (events || []).filter((e) =>
      e.workerId === startEvent.workerId
      && e.eventType === endType
      && new Date(e.timestamp) >= new Date(startEvent.timestamp)
    );
    sameWorker.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return sameWorker[0] || null;
  };

  const slotMinutesForDay = (workerId, date) => {
    const wh = workhoursByWorker.get(Number(workerId));
    if (!wh) return 0;
    const dow = jsDowToBackend(dowInZone(date, tzOf({ workerId })));
    const slot = wh.get ? wh.get(dow) : wh[dow];
    if (!slot) return 0;
    const hm = (v) => {
      if (!v) return null;
      const [h, m] = String(v).slice(0, 5).split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const in1 = hm(slot.in1), out1 = hm(slot.out1);
    const in2 = hm(slot.in2), out2 = hm(slot.out2);
    const d1 = in1 != null && out1 != null ? Math.max(0, out1 - in1) : 0;
    const d2 = in2 != null && out2 != null ? Math.max(0, out2 - in2) : 0;
    return d1 + d2;
  };

  // Abre el modal con la info pre-calculada. Buscamos el END para que el
  // modal sepa el rango exacto del periodo. workhoursByWorker llega como
  // Map<dow, slot> — lo aplanamos a array porque el helper computeAbsenceMinutes
  // del modal espera ese formato (es el mismo que devuelve `getWorkHours`).
  const openAbsenceApproval = (event) => {
    const isHourly = event.eventType === EVENT_TYPES.ABSENCE_START;
    const isFull = event.eventType === EVENT_TYPES.SICK_LEAVE_START;
    if (!isHourly && !isFull) return;
    const endType = isHourly ? EVENT_TYPES.ABSENCE_END : EVENT_TYPES.SICK_LEAVE_END;
    const endEvent = findAbsenceEnd(event, endType);
    const whMap = workhoursByWorker.get(Number(event.workerId));
    const whArray = whMap
      ? Array.from(whMap.values ? whMap.values() : [])
      : [];
    // Histórico de jornadas (railway). Si existe para este trabajador, el
    // modal lo prioriza sobre el workhours actual y resuelve la versión
    // vigente en la fecha de la ausencia. Si no, cae al workhours actual.
    const scheduleHistory = scheduleHistoryByWorker.get(Number(event.workerId)) || [];
    // El estado de carga del horario (cargando / fallo / listo) se pasa EN
    // VIVO al modal (ver JSX): mientras no esté listo no calcula horas.
    setAbsenceModal({
      event,
      endEvent,
      worker: event._worker || null,
      workhours: whArray,
      scheduleHistory,
    });
  };

  const handleAbsenceApproval = async (event, recoverable, absenceMinutes) => {
    // Identificar el tipo de par. Solo START tiene sentido como punto de
    // entrada (END lo aprobaremos automáticamente al mismo tiempo).
    const isAbsenceHourlyStart = event.eventType === EVENT_TYPES.ABSENCE_START;
    const isAbsenceFullStart = event.eventType === EVENT_TYPES.SICK_LEAVE_START;
    if (!isAbsenceHourlyStart && !isAbsenceFullStart) return;
    const endType = isAbsenceHourlyStart ? EVENT_TYPES.ABSENCE_END : EVENT_TYPES.SICK_LEAVE_END;
    const endEvent = findAbsenceEnd(event, endType);

    setActioningId(`event-${event.id}`);
    try {
      // 1) Actualizar START (geoInfo + approvalStatus) y END si existe.
      const targets = [event];
      if (endEvent) targets.push(endEvent);
      const updatedById = new Map();
      for (const target of targets) {
        const withApprover = buildGeoInfoWithApprover(target.geoInfo, user);
        const nextGeoInfo = buildGeoInfoWithRecoverable(withApprover, recoverable);
        await updateEvent(target.id, {
          approvalStatus: APPROVAL_STATUS.APPROVED,
          geoInfo: nextGeoInfo,
        });
        updatedById.set(target.id, nextGeoInfo);
      }

      // 2) Solo DÍA COMPLETO: si es recuperable y tenemos minutos, crear
      //    offset manual negativo. Lo redondeamos a 2 decimales para no
      //    acumular precisión. Los minutos vienen YA calculados desde el modal
      //    (cómputo multi-día que intersecta la jornada con el rango exacto
      //    del periodo). Las ausencias POR HORAS nunca crean offset: la bolsa
      //    ya las cuenta como deuda (se duplicaría).
      const debit = isAbsenceFullStart && recoverable && absenceMinutes > 0;
      if (debit) {
        const offsetHours = -Math.round((absenceMinutes / 60) * 100) / 100;
        const label = 'Día completo';
        const hh = Math.floor(absenceMinutes / 60);
        const mm = absenceMinutes % 60;
        const dur = mm === 0 ? `${hh}h` : `${hh}h ${String(mm).padStart(2, '0')}m`;
        await addHourBalance({
          workerId: event.workerId,
          // Día del ajuste = día de la ausencia en la zona del trabajador.
          date: dayKeyInZone(event.timestamp, tzOf(event)),
          reason: `${label} recuperable (${dur})`,
          offset: offsetHours,
        });
      }

      setEvents((prev) => prev.map((e) => {
        if (!updatedById.has(e.id)) return e;
        return { ...e, approvalStatus: APPROVAL_STATUS.APPROVED, geoInfo: updatedById.get(e.id) };
      }));
      toast.success(
        FEEDBACK.requestReviewed({
          approved: true,
          subject: requestSubject(event.eventType),
          workerName: workerNameOf(event),
          when: whenOf(event, endEvent),
        }) + (debit ? FEEDBACK.recoverableDebited(absenceMinutes) : '')
          + (isAbsenceHourlyStart ? FEEDBACK.hourlyAbsenceRecovered : ''),
      );
      // Sin catch a propósito: lo llama AbsenceApprovalModal y, si falla, el
      // error le llega al modal, que lo enseña y sigue abierto para reintentar.
    } finally {
      setActioningId(null);
    }
  };

  // Ausencia POR HORAS: se aprueba directamente (sin modal), START + END,
  // marcada `;recoverable=1` y sin offset. Ver comentario de arriba.
  const handleHourlyAbsenceApproval = async (event) => {
    try {
      await handleAbsenceApproval(event, true, 0);
    } catch (err) {
      toast.error(err, 'No se pudo guardar la decisión.');
    }
  };

  const handleEventApproval = async (event, status, opts = {}) => {
    // Las solicitudes de eliminación tienen su propio handler que actúa
    // sobre los DOS extremos del periodo. No deben pasar por el flujo
    // genérico de aprobación de eventos.
    if (isEventDeleteRequest(event)) {
      return handleDeleteRequest(event, status === APPROVAL_STATUS.APPROVED);
    }
    if (status === APPROVAL_STATUS.APPROVED) {
      const ok = await confirmVacationCapOk(event);
      if (!ok) return;
    }
    setActioningId(`event-${event.id}`);
    try {
      // Sea aprobación o rechazo, dejamos traza de quién y cuándo. El
      // mismo flag se usa tanto para APPROVED como para REJECTED — luego
      // el lector (InformeHorarioView, ConsultaFichajesView) lo enseña
      // como "Aprobado por X" o "Rechazado por X" según `approvalStatus`.
      let nextGeoInfo = buildGeoInfoWithApprover(event.geoInfo, user);
      if (status === APPROVAL_STATUS.APPROVED && Object.prototype.hasOwnProperty.call(opts, 'countAsExtra')) {
        const flag = opts.countAsExtra ? ';extra=1' : ';extra=0';
        const cleaned = nextGeoInfo.replace(/;extra=[01]/g, '');
        nextGeoInfo = cleaned + flag;
      }
      const body = { approvalStatus: status, geoInfo: nextGeoInfo };
      await updateEvent(event.id, body);
      setEvents((prev) => prev.map((e) =>
        e.id === event.id
          ? { ...e, approvalStatus: status, geoInfo: nextGeoInfo }
          : e
      ));
      const approved = status === APPROVAL_STATUS.APPROVED;
      const extraNote = approved && Object.prototype.hasOwnProperty.call(opts, 'countAsExtra')
        ? (opts.countAsExtra ? ' Cuenta como horas extra.' : ' Cuenta como jornada normal.')
        : '';
      toast.success(FEEDBACK.requestReviewed({
        approved,
        subject: requestSubject(event.eventType),
        workerName: workerNameOf(event),
        when: whenOf(event),
      }) + extraNote);
    } catch (err) {
      toast.error(err, 'No se pudo guardar la decisión.');
    } finally {
      setActioningId(null);
    }
  };

  // Aprueba/rechaza los DOS extremos de un periodo en una sola operación.
  // Si solo hay uno (item.kind === 'single') redirige al handler normal.
  // Para ausencias justificadas con START + END, el modal de recuperables
  // se invoca por separado desde el botón verde — este handler cubre el
  // resto: vacaciones, días completos sin modal, ausencias por horas sin
  // modal, y el "Rechazar" de todos.
  const handlePeriodApproval = async (item, status) => {
    if (item.kind !== 'period') {
      return handleEventApproval(item.event, status);
    }
    const { start, end } = item;
    if (isPeriodDeleteRequest(start) || isPeriodDeleteRequest(end)) {
      return handleDeleteRequest(isPeriodDeleteRequest(start) ? start : end, status === APPROVAL_STATUS.APPROVED);
    }
    if (status === APPROVAL_STATUS.APPROVED) {
      const ok = await confirmVacationCapOk(start, end);
      if (!ok) return;
    }
    setActioningId(`period-${start.id}`);
    try {
      const startGeo = buildGeoInfoWithApprover(start.geoInfo, user);
      const endGeo = buildGeoInfoWithApprover(end.geoInfo, user);
      await updateEvent(start.id, { approvalStatus: status, geoInfo: startGeo });
      await updateEvent(end.id,   { approvalStatus: status, geoInfo: endGeo });
      setEvents((prev) => prev.map((e) => {
        if (e.id === start.id) return { ...e, approvalStatus: status, geoInfo: startGeo };
        if (e.id === end.id)   return { ...e, approvalStatus: status, geoInfo: endGeo };
        return e;
      }));
      toast.success(FEEDBACK.requestReviewed({
        approved: status === APPROVAL_STATUS.APPROVED,
        subject: requestSubject(start.eventType),
        workerName: workerNameOf(start),
        when: whenOf(start, end),
      }));
    } catch (err) {
      toast.error(err, 'No se pudo guardar la decisión.');
    } finally {
      setActioningId(null);
    }
  };

  const handleCorrectionApproval = async (correction, status) => {
    setActioningId(`correction-${correction.id}`);
    try {
      await reviewCorrection(correction.id, status);
      setCorrections((prev) => prev.map((c) => c.id === correction.id ? { ...c, approvalStatus: status } : c));
      toast.success(FEEDBACK.requestReviewed({
        approved: status === APPROVAL_STATUS.APPROVED,
        subject: 'corrección',
        workerName: workerNameOf(correction),
        when: correction.timestamp ? whenOf({ ...correction, eventType: correction.eventType ?? EVENT_TYPES.CLOCK_IN }) : null,
      }));
    } catch (err) {
      toast.error(err, 'No se pudo guardar la decisión.');
    } finally {
      setActioningId(null);
    }
  };

  // Eliminar fichaje original referenciado por la corrección. Tras borrar el
  // pikachu, marcamos la corrección como rechazada (ya no aplica) — así sale
  // del listado de pendientes pero queda traza de la solicitud.
  const handleCorrectionDeleteOriginal = async (correction, originalEvent) => {
    if (!originalEvent?.id) {
      toast.error('No se encuentra el fichaje original referenciado.');
      return;
    }
    const ok = window.confirm(
      `¿Eliminar definitivamente el fichaje original "${EVENT_TYPE_LABELS[originalEvent.eventType] || originalEvent.eventType}" del ${fmtFull(originalEvent.timestamp, tzOf(originalEvent))}? Esta acción es irreversible.`
    );
    if (!ok) return;
    setActioningId(`correction-${correction.id}`);
    try {
      await deleteEvent(originalEvent.id);
      // Aunque el delete del fichaje funcione, la corrección queda obsoleta:
      // la marcamos como rechazada para sacarla de pendientes. Si el reviewCorrection
      // falla (no debería, pero por si acaso), no rompemos la UX — el delete ya
      // pasó — pero se avisa: la corrección seguirá saliendo como pendiente.
      let closed = true;
      try {
        await reviewCorrection(correction.id, APPROVAL_STATUS.REJECTED);
      } catch (_) { closed = false; }
      setEvents((prev) => prev.filter((e) => e.id !== originalEvent.id));
      setCorrections((prev) => prev.map((c) =>
        c.id === correction.id ? { ...c, approvalStatus: APPROVAL_STATUS.REJECTED } : c
      ));
      if (closed) toast.success(FEEDBACK.correctionOriginalDeleted);
      else toast.warning(FEEDBACK.correctionNotClosed);
    } catch (err) {
      toast.error(err, 'No se pudo eliminar el fichaje original.');
    } finally {
      setActioningId(null);
    }
  };

  // Renderiza un periodo agrupado (START + END). Un solo botón "Aprobar"
  // afecta a ambos extremos. Para día completo (sick leave) el botón verde
  // abre el modal de recuperables — el modal ya busca el endEvent y aprueba
  // ambos en su handler. Por horas: se aprueba directo (se recupera siempre).
  const renderPeriodItem = (item) => {
    const { start, end } = item;
    const meta = EVENT_META[start.eventType] || { label: '?', icon: Clock, color: 'text-ink' };
    const Icon = meta.icon;
    const w = item._worker;
    const isAbsenceStart = start.eventType === EVENT_TYPES.SICK_LEAVE_START
      || start.eventType === EVENT_TYPES.ABSENCE_START;
    const isVacation = start.eventType === EVENT_TYPES.VACATION_START;
    const isBaja = start.eventType === EVENT_TYPES.SICK_LEAVE_START;
    const isDeleteRequest = isPeriodDeleteRequest(start) || isPeriodDeleteRequest(end);
    const startCreated = new Date(start.createdAt || start.timestamp).getTime();
    const workerDocs = docsByUploader.get(Number(w?.id)) || [];
    const docsToShow = isBaja
      ? workerDocs.filter((d) => {
          const dt = new Date(d.createdAt || 0).getTime();
          return Math.abs(dt - startCreated) <= 10 * 60 * 1000;
        })
      : [];
    const isActioning = actioningId === `period-${start.id}`;

    // Etiqueta del periodo: igual nombre que el evento pero sin "Inicio".
    // Si es vacaciones → "Vacaciones". Si día completo → "Día completo".
    // Si por horas → "Por horas". Si compensación → "Compensación".
    const periodLabel = (() => {
      if (isVacation) return 'Vacaciones';
      if (isBaja) return 'Día completo';
      if (start.eventType === EVENT_TYPES.ABSENCE_START) return 'Por horas';
      if (start.eventType === EVENT_TYPES.COMPENSATION_START) return 'Compensación';
      return meta.label;
    })();

    // Texto compacto del rango. Para vacaciones / día completo mostramos
    // solo la fecha (no la hora exacta del registro). Para "por horas",
    // mostramos también la hora del inicio y fin porque define la duración.
    // Días y horas en la zona del TRABAJADOR (un periodo creado a las 00:00
    // de Madrid no debe verse empezando el día anterior en otro navegador).
    const periodTz = tzOf(start);
    const dateOnly = (iso) => {
      try {
        return fmtDayKey(dayKeyInZone(iso, periodTz), { weekday: 'short', day: 'numeric', month: 'short' }) || iso;
      } catch { return iso; }
    };
    const hourly = start.eventType === EVENT_TYPES.ABSENCE_START;
    const rangeText = hourly ? `${fmtFull(start.timestamp, periodTz)} → ${fmtFull(end.timestamp, periodTz)}`
                             : `${dateOnly(start.timestamp)} → ${dateOnly(end.timestamp)}`;

    // Cuántos días naturales cubre el periodo (informativo), en días del trabajador.
    const dayCount = Math.max(1, dayKeysBetween(
      dayKeyInZone(start.timestamp, periodTz), dayKeyInZone(end.timestamp, periodTz),
    ).length);

    return (
      <li key={`period-${start.id}`} className="py-3">
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center ${meta.color}`}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="font-semibold text-ink">{periodLabel}</span>
              {isDeleteRequest && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-danger/15 text-danger"
                  title="El trabajador ha pedido eliminar este periodo. Si apruebas, se borrará por completo."
                >
                  <Trash2 size={10} /> Solicitud de eliminación
                </span>
              )}
              <span className="text-ink-muted">·</span>
              <Link to={`/panel/equipo/${w?.id}`} className="font-semibold text-primary-darker hover:underline truncate">
                {w?.name || `Trabajador #${start.workerId}`}
              </Link>
              {w?.type != null && (
                <span className="text-xs text-ink-muted">{USER_TYPE_LABELS[w.type]}</span>
              )}
              {w?.workingSite != null && workplacesById.get(Number(w.workingSite)) && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-ink-muted">
                  {workplacesById.get(Number(w.workingSite))?.name}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-ink-muted">
                {dayCount === 1 ? '1 día' : `${dayCount} días`}
              </span>
            </div>
            <div className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200">
              <Clock size={12} className="text-ink-muted" />
              <span className="text-sm font-semibold text-ink tabular-nums capitalize">{rangeText}</span>
            </div>
            {(() => {
              const motivo = parseMotivo(start.geoInfo);
              if (!motivo) return null;
              return (
                <div className="mt-1.5 text-xs text-ink bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
                  <span className="font-semibold text-ink-muted">Motivo: </span>
                  {motivo}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handlePeriodApproval(item, APPROVAL_STATUS.REJECTED)}
              disabled={isActioning}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-danger border border-danger/30 hover:bg-danger/5 transition-colors disabled:opacity-60"
            >
              <X size={12} /> Rechazar
            </button>
            {isAbsenceStart && !isDeleteRequest ? (
              <button
                onClick={() => (start.eventType === EVENT_TYPES.ABSENCE_START
                  ? handleHourlyAbsenceApproval(start)
                  : openAbsenceApproval(start))}
                disabled={isActioning}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-success hover:bg-emerald-700 transition-colors disabled:opacity-60"
                title={start.eventType === EVENT_TYPES.ABSENCE_START
                  ? HOURLY_ABSENCE_APPROVE_TITLE
                  : 'Aprobar la ausencia. Te abrirá un modal donde podrás decidir si las horas se descuentan de la bolsa o no.'}
              >
                {isActioning ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Aprobar</>}
              </button>
            ) : (
              <button
                onClick={() => handlePeriodApproval(item, APPROVAL_STATUS.APPROVED)}
                disabled={isActioning}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-60 ${
                  isDeleteRequest ? 'bg-danger hover:bg-red-700' : 'bg-success hover:bg-emerald-700'
                }`}
              >
                {isActioning
                  ? <Loader2 size={12} className="animate-spin" />
                  : isDeleteRequest
                    ? <><Trash2 size={12} /> Aprobar eliminación</>
                    : <><Check size={12} /> Aprobar</>}
              </button>
            )}
          </div>
        </div>

        {isBaja && (
          <div className="ml-14 mt-2">
            {docsToShow.length === 0 ? (
              <div className="inline-flex items-center gap-1.5 text-xs text-warning bg-warning/10 px-2 py-1 rounded-md">
                <AlertTriangle size={11} /> Sin justificante adjunto.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {docsToShow.map((d) => {
                  const Icn = fileIcon(d.filename);
                  return (
                    <button
                      key={d.id}
                      onClick={() => triggerDownload(d, onDownloadError)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-soft text-primary-darker hover:bg-primary-soft/70 transition-colors"
                      title={d.filename}
                    >
                      <Icn size={12} />
                      <span className="max-w-[180px] truncate">{d.filename}</span>
                      <Download size={11} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderEventItem = (item) => {
    // visibleItems devuelve wrappers — desenvolvemos al renderer correcto.
    if (item?.kind === 'period') return renderPeriodItem(item);
    const e = item?.kind === 'single' ? item.event : item;
    const meta = EVENT_META[e.eventType] || { label: '?', icon: Clock, color: 'text-ink' };
    const Icon = meta.icon;
    const w = e._worker;
    const isBaja = e.eventType === EVENT_TYPES.SICK_LEAVE_START || e.eventType === EVENT_TYPES.SICK_LEAVE_END;
    const isClock = e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT;
    // El aviso de "día de descanso → cuenta como extras" solo tiene sentido
    // cuando se está APROBANDO el fichaje. En una solicitud de eliminación se
    // decide otra cosa (borrarlo o no), así que estorba.
    const onRest = isClock && !isAnyDeleteRequest(e) && eventOnRestDay(e);
    // Para los START de día completo el admin elige si las horas son
    // recuperables (descontan de la bolsa) o no. Los de por horas se
    // aprueban sin preguntar (se recuperan siempre). Los END se aprueban en
    // paralelo dentro del mismo handler — no hace falta UI extra.
    const isAbsenceStart = e.eventType === EVENT_TYPES.SICK_LEAVE_START
      || e.eventType === EVENT_TYPES.ABSENCE_START;
    // Solicitud de eliminación de vacaciones (workaround geoInfo): se trata
    // como un caso especial — etiqueta visible y handler distinto al
    // aprobar/rechazar (handleDeleteRequest borra los dos extremos
    // del periodo en lugar de cambiar approvalStatus).
    const isDeleteRequest = isEventDeleteRequest(e);
    // La de un fichaje suelto se explica distinto: no se borra un periodo del
    // calendario, se borra ESE fichaje (que hasta entonces sigue contando).
    const isClockDeleteReq = isClockDeleteRequest(e);
    const eventCreated = new Date(e.createdAt || e.timestamp).getTime();
    const workerDocs = docsByUploader.get(Number(w?.id)) || [];
    const docsToShow = isBaja
      ? workerDocs.filter((d) => {
          const dt = new Date(d.createdAt || 0).getTime();
          return Math.abs(dt - eventCreated) <= 10 * 60 * 1000;
        })
      : [];
    const isActioning = actioningId === `event-${e.id}`;

    return (
      <li key={`event-${e.id}`} className="py-3">
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center ${meta.color}`}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="font-semibold text-ink">{meta.label}</span>
              {isDeleteRequest && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-danger/15 text-danger"
                  title={isClockDeleteReq
                    ? 'El trabajador ha pedido eliminar este fichaje, que ya está aprobado. Sigue contando en su jornada y en su bolsa: si apruebas, se borra; si rechazas, se queda como está.'
                    : 'El trabajador ha pedido eliminar este periodo. Si apruebas, se borrará por completo.'}
                >
                  <Trash2 size={10} /> Solicitud de eliminación
                </span>
              )}
              <span className="text-ink-muted">·</span>
              <Link to={`/panel/equipo/${w?.id}`} className="font-semibold text-primary-darker hover:underline truncate">
                {w?.name || `Trabajador #${e.workerId}`}
              </Link>
              {w?.type != null && (
                <span className="text-xs text-ink-muted">{USER_TYPE_LABELS[w.type]}</span>
              )}
              {w?.workingSite != null && workplacesById.get(Number(w.workingSite)) && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-ink-muted">
                  {workplacesById.get(Number(w.workingSite))?.name}
                </span>
              )}
              {onRest && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-warning/15 text-warning"
                  title="Este fichaje cayó en un día que el trabajador tiene como descanso. Si lo apruebas, las horas se contabilizarán como extras automáticamente."
                >
                  <AlertTriangle size={10} /> Día de descanso · cuenta como extras
                </span>
              )}
              {isDeferred(e) && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                  title="Fichaje registrado a posteriori (diferido). La fecha es cuando quedó guardado en el sistema."
                >
                  Diferido · <span className="font-mono normal-case tracking-normal">{deferredLabel(e)}</span>
                </span>
              )}
            </div>
            <div className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200">
              <Clock size={12} className="text-ink-muted" />
              <span className="text-sm font-semibold text-ink tabular-nums capitalize">{fmtFull(e.timestamp, tzOf(e))}</span>
            </div>
            {/* Ubicación del fichaje manual: misma celda que en el detalle del
                trabajador (Equipo). Muestra la dirección desde el geoInfo y, si
                la sede tiene coordenadas, marca en rojo "(fuera de la sede)". */}
            {isClock && (
              <div className="mt-1.5">
                <LocationCell
                  geoInfo={e.geoInfo}
                  workplace={w?.workingSite != null ? workplacesById.get(Number(w.workingSite)) : null}
                />
              </div>
            )}
            {/* Motivo del fichaje manual (cuando el trabajador escribió una
                nota al añadirlo desde el Dashboard). Mismo estilo que la
                "razón" que mostramos en las correcciones, para que el admin
                tenga ambos casos visualmente iguales. */}
            {(() => {
              const motivo = parseMotivo(e.geoInfo);
              if (!motivo) return null;
              return (
                <div className="mt-1.5 text-xs text-ink bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
                  <span className="font-semibold text-ink-muted">Motivo: </span>
                  {motivo}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleEventApproval(e, APPROVAL_STATUS.REJECTED)}
              disabled={isActioning}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-danger border border-danger/30 hover:bg-danger/5 transition-colors disabled:opacity-60"
              title={isDeleteRequest
                ? 'Rechazar la eliminación: se retira la solicitud y el registro se queda tal cual.'
                : ''}
            >
              <X size={12} /> Rechazar
            </button>
            {onRest ? (
              // En día de descanso, el admin decide explícitamente si lo
              // contabiliza como horas extras o como jornada normal de
              // sustitución (ej. cubrir a un compañero, sin acumular extras).
              <>
                <button
                  onClick={() => handleEventApproval(e, APPROVAL_STATUS.APPROVED, { countAsExtra: false })}
                  disabled={isActioning}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-success hover:bg-emerald-700 transition-colors disabled:opacity-60"
                  title="Aprobar como jornada normal: las horas no se acumulan como extras (el trabajador cubrió un día por causa puntual)."
                >
                  {isActioning ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Como normal</>}
                </button>
                <button
                  onClick={() => handleEventApproval(e, APPROVAL_STATUS.APPROVED, { countAsExtra: true })}
                  disabled={isActioning}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-warning hover:bg-amber-700 transition-colors disabled:opacity-60"
                  title="Aprobar como horas extras: las horas se acumulan al saldo de extras del trabajador."
                >
                  {isActioning ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Como extras</>}
                </button>
              </>
            ) : isAbsenceStart && !isDeleteRequest ? (
              // START de ausencia justificada: un solo botón "Aprobar" que
              // abre un modal de confirmación con:
              //  · El rango exacto del periodo (multi-día si aplica).
              //  · Las horas calculadas según la JORNADA del trabajador
              //    (no 24h, no solo el día del START — itera día a día
              //    intersectando con el slot vigente).
              //  · La bolsa actual y cómo quedaría con/sin descuento.
              //  · Dos botones de confirmación con colores intuitivos:
              //    verde "No descontar" / rojo "Descontar X horas".
              // Solo DÍA COMPLETO: la ausencia POR HORAS se aprueba sin modal
              // (se recupera siempre, handleHourlyAbsenceApproval).
              <button
                onClick={() => (e.eventType === EVENT_TYPES.ABSENCE_START
                  ? handleHourlyAbsenceApproval(e)
                  : openAbsenceApproval(e))}
                disabled={isActioning}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-success hover:bg-emerald-700 transition-colors disabled:opacity-60"
                title={e.eventType === EVENT_TYPES.ABSENCE_START
                  ? HOURLY_ABSENCE_APPROVE_TITLE
                  : 'Aprobar la ausencia. Te abrirá un modal donde podrás decidir si las horas se descuentan de la bolsa o no.'}
              >
                {isActioning ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Aprobar</>}
              </button>
            ) : (
              <button
                onClick={() => handleEventApproval(e, APPROVAL_STATUS.APPROVED)}
                disabled={isActioning}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-60 ${
                  isDeleteRequest ? 'bg-danger hover:bg-red-700' : 'bg-success hover:bg-emerald-700'
                }`}
                title={isDeleteRequest
                  ? (isClockDeleteReq
                    ? 'Borrar definitivamente este fichaje del historial del trabajador'
                    : 'Borrar definitivamente este periodo del calendario del trabajador')
                  : ''}
              >
                {isActioning
                  ? <Loader2 size={12} className="animate-spin" />
                  : isDeleteRequest
                    ? <><Trash2 size={12} /> Aprobar eliminación</>
                    : <><Check size={12} /> Aprobar</>}
              </button>
            )}
          </div>
        </div>

        {isBaja && (
          <div className="ml-14 mt-2">
            {docsToShow.length === 0 ? (
              <div className="inline-flex items-center gap-1.5 text-xs text-warning bg-warning/10 px-2 py-1 rounded-md">
                <AlertTriangle size={11} /> Sin justificante adjunto.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {docsToShow.map((d) => {
                  const Icn = fileIcon(d.filename);
                  return (
                    <button
                      key={d.id}
                      onClick={() => triggerDownload(d, onDownloadError)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-soft text-primary-darker hover:bg-primary-soft/70 transition-colors"
                      title={d.filename}
                    >
                      <Icn size={12} />
                      <span className="max-w-[180px] truncate">{d.filename}</span>
                      <Download size={11} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderCorrectionItem = (c) => {
    const w = c._worker;
    // El "antes" se reconstruye buscando el pikachu original por pikachuId
    // (toleramos pikachu_id) dentro de los eventos cargados (últimos 3 meses).
    const pikachuId = c.pikachuId ?? c.pikachu_id ?? null;
    const originalEvent = pikachuId
      ? events.find((e) => Number(e.id) === Number(pikachuId)) || null
      : null;
    const fromType = originalEvent?.eventType ?? null;
    const fromTs = originalEvent?.timestamp ?? null;
    const toType = c.eventType;
    const toTs = c.timestamp;
    const id = `correction-${c.id}`;

    return (
      <li key={id} className="py-3">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-primary">
            <Wrench size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="font-semibold text-ink">Solicitud de corrección</span>
              <span className="text-ink-muted">·</span>
              <Link to={`/panel/equipo/${w?.id}`} className="font-semibold text-primary-darker hover:underline truncate">
                {w?.name || `Trabajador #${c.workerId}`}
              </Link>
              {w?.type != null && (
                <span className="text-xs text-ink-muted">{USER_TYPE_LABELS[w.type]}</span>
              )}
              {w?.workingSite != null && workplacesById.get(Number(w.workingSite)) && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-ink-muted">
                  {workplacesById.get(Number(w.workingSite))?.name}
                </span>
              )}
            </div>

            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-100 text-ink-muted line-through decoration-danger/60 decoration-2">
                <span className="text-sm font-semibold not-italic">{EVENT_TYPE_LABELS[fromType] ?? '—'}</span>
                <span className="text-ink-muted/60">·</span>
                <span className="text-sm tabular-nums capitalize">{fromTs ? fmtFull(fromTs, tzOf(c)) : '—'}</span>
              </div>
              <ArrowRight size={14} className="text-ink-muted" />
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-success/10 text-success">
                <span className="text-sm font-semibold">{EVENT_TYPE_LABELS[toType] ?? '—'}</span>
                <span className="text-success/60">·</span>
                <span className="text-sm tabular-nums capitalize">{toTs ? fmtFull(toTs, tzOf(c)) : '—'}</span>
              </div>
            </div>

            {/* El motivo puede llevar al final la ubicación desde la que se
                pidió la corrección ([geo:…], ver appendGeoToReason en
                lib/geoInfo): se separa y se pinta como ubicación. */}
            {(() => {
              const { text, geoInfo: reqGeo } = splitReasonGeo(c.reason);
              return (
                <>
                  {text.trim() && (
                    <div className="mt-1.5 text-xs text-ink bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
                      <span className="font-semibold text-ink-muted">Motivo: </span>
                      {text.trim()}
                    </div>
                  )}
                  {reqGeo && (
                    <div className="mt-1.5">
                      <LocationCell
                        geoInfo={reqGeo}
                        workplace={w?.workingSite != null ? workplacesById.get(Number(w.workingSite)) : null}
                      />
                    </div>
                  )}
                </>
              );
            })()}
            {c.createdAt && (
              <div className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200">
                <Clock size={12} className="text-ink-muted" />
                <span className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Solicitado</span>
                <span className="text-sm font-semibold text-ink tabular-nums capitalize">{fmtFull(c.createdAt, tzOf(c))}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {originalEvent && canDeleteEvent(user, w) && (
              <button
                onClick={() => handleCorrectionDeleteOriginal(c, originalEvent)}
                disabled={actioningId === id}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-700 border border-red-300 hover:bg-red-50 transition-colors disabled:opacity-60"
                title="Eliminar el fichaje original — la corrección queda rechazada"
              >
                <Trash2 size={12} /> Eliminar fichaje
              </button>
            )}
            <button
              onClick={() => handleCorrectionApproval(c, APPROVAL_STATUS.REJECTED)}
              disabled={actioningId === id}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-danger border border-danger/30 hover:bg-danger/5 transition-colors disabled:opacity-60"
            >
              <X size={12} /> Rechazar
            </button>
            <button
              onClick={() => handleCorrectionApproval(c, APPROVAL_STATUS.APPROVED)}
              disabled={actioningId === id}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-success hover:bg-emerald-700 transition-colors disabled:opacity-60"
            >
              {actioningId === id ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Aprobar</>}
            </button>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Administración</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Aprobaciones pendientes</h1>
        </div>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      <section className="pb-5 mb-5 border-b border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Inbox size={11} className="text-warning" /> Total pendientes
            </div>
            <div className="font-display text-2xl font-bold text-warning tabular-nums">{totalPending}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Wrench size={11} className="text-primary" /> Correcciones
            </div>
            <div className="font-display text-2xl font-bold text-primary-darker tabular-nums">{counts.correcciones}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Plane size={11} className="text-accent-dark" /> Vacaciones
            </div>
            <div className="font-display text-2xl font-bold text-accent-dark tabular-nums">{counts.vacaciones}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <UserMinus size={11} className="text-danger" /> Días completos
            </div>
            <div className="font-display text-2xl font-bold text-danger tabular-nums">{counts.bajas}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Clock size={11} className="text-primary" /> Manuales
            </div>
            <div className="font-display text-2xl font-bold text-primary-darker tabular-nums">{counts.manuales}</div>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-1 mb-3 border-b border-slate-200">
        {TABS.map((t) => {
          const Icn = t.icon;
          const active = tab === t.key;
          const count = counts[t.key];
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              <Icn size={14} />
              {t.label}
              {count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  active ? 'bg-primary text-white' : 'bg-slate-100 text-ink-muted'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr] gap-3 items-end">
        {companiesById.size > 1 && (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Empresa</label>
            <select
              value={filterCompanyId}
              onChange={(e) => {
                setFilterCompanyId(e.target.value);
                // Reseteamos trabajador/sede al cambiar empresa para que el
                // listado de los selectores de abajo no muestre opciones
                // incompatibles con la selección actual.
                setFilterWorkerId('');
                setFilterWorkplaceId('');
              }}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
            >
              <option value="">Todas</option>
              {Array.from(companiesById.entries())
                .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
                .map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Trabajador</label>
          <select
            value={filterWorkerId}
            onChange={(e) => setFilterWorkerId(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
          >
            <option value="">Todos</option>
            {workers
              .filter((w) => !filterCompanyId || Number(w.companyId) === Number(filterCompanyId))
              .map((w) => (
                <option key={w.id} value={w.id}>{w.name || `#${w.id}`}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Sede</label>
          <select
            value={filterWorkplaceId}
            onChange={(e) => setFilterWorkplaceId(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
          >
            <option value="">Todas</option>
            {Array.from(workplacesById.values()).map((wp) => (
              <option key={wp.id} value={wp.id}>{wp.name || `Sede #${wp.id}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Desde</label>
          <input
            type="date"
            value={filterDateStart}
            max={filterDateEnd || undefined}
            onChange={(e) => setFilterDateStart(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Hasta</label>
          <input
            type="date"
            value={filterDateEnd}
            min={filterDateStart || undefined}
            onChange={(e) => setFilterDateEnd(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Buscar</label>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              placeholder="Nombre, email o NIF"
              className="w-full pl-7 pr-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-primary"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setFilterCompanyId(''); setFilterWorkerId(''); setFilterWorkplaceId(''); setFilterDateStart(''); setFilterDateEnd(''); setFilterQuery(''); }}
          className="px-3 py-1.5 rounded-md text-xs font-semibold text-ink-muted hover:bg-slate-100 transition-colors"
        >
          Limpiar filtros
        </button>
      </div>

      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 size={18} className="animate-spin text-primary" />
            <span className="text-sm">Cargando pendientes…</span>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2 text-ink-muted">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Inbox size={22} />
            </div>
            <div className="text-sm">No hay solicitudes pendientes en esta pestaña.</div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleItems.map((item) =>
              tab === 'correcciones' ? renderCorrectionItem(item) : renderEventItem(item)
            )}
          </ul>
        )}
      </div>

      <AbsenceApprovalModal
        open={!!absenceModal}
        event={absenceModal?.event}
        endEvent={absenceModal?.endEvent}
        worker={absenceModal?.worker}
        workhours={absenceModal?.workhours}
        scheduleHistory={absenceModal
          ? (scheduleHistoryByWorker.get(Number(absenceModal.event.workerId)) || absenceModal.scheduleHistory)
          : undefined}
        scheduleStatus={absenceModal
          ? (workhoursFailed.has(Number(absenceModal.event.workerId)) ? 'error' : (scheduleHistoryLoaded ? 'ready' : 'loading'))
          : 'ready'}
        onClose={() => setAbsenceModal(null)}
        onConfirm={async (recoverable, absenceMinutes) => {
          await handleAbsenceApproval(absenceModal.event, recoverable, absenceMinutes);
          setAbsenceModal(null);
        }}
      />
    </div>
  );
};

export default AprobacionesPage;
